import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { IndianRupee } from "lucide-react";

export default function AdminRefundSettings() {
  const [mode, setMode] = useState("manual");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/config/refund-mode").then((r) => setMode(r.data.mode || "manual")).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async (v) => {
    setMode(v);
    setSaving(true);
    try {
      await api.put("/admin/config/refund-mode", { mode: v });
      toast.success(v === "automatic" ? "Refunds will go out automatically via Razorpay on cancellation" : "Refunds set to manual — you approve each one");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save refund mode");
    } finally { setSaving(false); }
  };

  if (loading) return null;
  return (
    <div data-testid="admin-refund-settings" className="mt-6 p-5 bg-white border border-gray-200 rounded-2xl max-w-xl">
      <div className="flex items-center gap-2 mb-1">
        <IndianRupee className="h-4 w-4 text-[#F48E2E]" />
        <div className="font-medium text-sm">Refund Mode (online payments)</div>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Applies when a paid online order is cancelled or a return-refund is fulfilled. COD and bank-transfer refunds are always recorded manually.
      </p>
      <RadioGroup value={mode} onValueChange={save} className="space-y-2" disabled={saving}>
        <label className="flex items-start gap-3 p-3 border rounded-xl cursor-pointer" data-testid="refund-mode-manual">
          <RadioGroupItem value="manual" />
          <div>
            <div className="font-medium text-sm">Manual <span className="text-[10px] text-gray-500">(default)</span></div>
            <div className="text-[11px] text-gray-500">No automatic refund. Cancelled paid orders show a "Process Refund" button in the orders list for you to trigger when ready.</div>
          </div>
        </label>
        <label className="flex items-start gap-3 p-3 border rounded-xl cursor-pointer" data-testid="refund-mode-automatic">
          <RadioGroupItem value="automatic" />
          <div>
            <div className="font-medium text-sm">Automatic</div>
            <div className="text-[11px] text-gray-500">Full order amount is refunded via Razorpay the moment a paid online order is cancelled. Failures are flagged for manual retry.</div>
          </div>
        </label>
      </RadioGroup>
    </div>
  );
}
